import { useCallback, useEffect, useState } from "react";
import { Newspaper, RefreshCw, ExternalLink, Radio, Clock } from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stripHtml = (text) => text.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();

const formatDate = (iso) => {
    try {
        return new Date(iso).toLocaleString("pt-BR", {
            day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return "";
    }
};

export const NewsSection = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await fetch(`${API}/news?limit=6`);
            if (!res.ok) throw new Error();
            const data = await res.json();
            setItems(data.items || []);
            setError(false);
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const timer = setInterval(load, 5 * 60 * 1000);
        return () => clearInterval(timer);
    }, [load]);

    return (
        <section id="noticias" data-testid="news-section" className="relative py-20 lg:py-32 bg-[#F8FAFC] overflow-hidden">
            <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none" aria-hidden="true" />
            <div className="absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full gold-glow animate-drift-slow pointer-events-none" aria-hidden="true" />
            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
                    <div className="max-w-2xl reveal-on-scroll">
                        <p className="eyebrow text-xs font-bold uppercase tracking-[0.2em] text-blue-900" data-testid="news-eyebrow">
                            <Radio className="w-4 h-4" />
                            Notícias em tempo real
                        </p>
                        <h1 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900" data-testid="news-title">
                            Fique por dentro das mudanças tributárias
                        </h1>
                        <p className="mt-4 text-base text-slate-600 leading-relaxed" data-testid="news-subtitle">
                            Acompanhe as novidades fiscais e contábeis que impactam a sua empresa, direto dos
                            principais portais do setor.
                        </p>
                    </div>
                    <button
                        onClick={load}
                        disabled={loading}
                        data-testid="news-refresh-button"
                        className="shrink-0 inline-flex items-center gap-2 text-sm font-bold text-blue-900 hover:text-blue-950 border border-blue-300 hover:border-blue-400 rounded-lg px-4 py-2.5 transition-colors duration-200 disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                        Atualizar
                    </button>
                </div>

                {error && (
                    <p data-testid="news-error" className="mt-10 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-5 py-4">
                        Não foi possível carregar as notícias agora. Tente atualizar em alguns instantes.
                    </p>
                )}

                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="news-grid">
                    {loading && items.length === 0
                        ? [...Array(6)].map((_, i) => (
                            <div key={i} className="bg-white border border-slate-200 rounded-xl p-6 space-y-4" data-testid={`news-skeleton-${i}`}>
                                <Skeleton className="h-4 w-28" />
                                <Skeleton className="h-5 w-full" />
                                <Skeleton className="h-5 w-3/4" />
                                <Skeleton className="h-16 w-full" />
                            </div>
                        ))
                        : items.map((news, i) => (
                            <article
                                key={news.id || news.url}
                                data-testid={`news-card-${i}`}
                                className="group glass-card rounded-xl p-6 flex flex-col transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg"
                            >
                                <div className="flex items-center justify-between gap-3">
                                    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-blue-900 bg-blue-50 border border-blue-200 rounded-full px-3 py-1" data-testid={`news-source-${i}`}>
                                        {news.source}
                                    </span>
                                    <span className="flex items-center gap-1.5 text-xs text-slate-400" data-testid={`news-date-${i}`}>
                                        <Clock className="w-3.5 h-3.5" />
                                        {formatDate(news.published_at)}
                                    </span>
                                </div>
                                <a
                                    href={news.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`news-link-${i}`}
                                    className="mt-4 block"
                                >
                                    <h3 className="text-base font-semibold text-slate-900 leading-snug group-hover:text-blue-900 transition-colors duration-200 flex items-start gap-2">
                                        {news.title}
                                        <ExternalLink className="w-4 h-4 mt-0.5 shrink-0 text-slate-300 group-hover:text-blue-900 transition-colors duration-200" />
                                    </h3>
                                </a>
                                {news.summary && (
                                    <p className="mt-3 text-sm text-slate-500 leading-relaxed line-clamp-3 flex-1">
                                        {stripHtml(news.summary)}
                                    </p>
                                )}
                            </article>
                        ))}
                </div>

                {!loading && !error && items.length === 0 && (
                    <div className="mt-12 flex flex-col items-center gap-3 text-slate-400" data-testid="news-empty">
                        <Newspaper className="w-8 h-8" />
                        <p className="text-sm">Nenhuma notícia disponível no momento.</p>
                    </div>
                )}

                <p className="mt-8 text-xs text-slate-400" data-testid="news-credit">
                    Conteúdo dos portais Portal Contábeis e Jornal Contábil. As notícias abrem no site de origem.
                </p>
            </div>
        </section>
    );
};
